import { AiAdmin } from '@vitesse/proto/vitesse/ai/v1/ai_admin_pb.js';
import { LitElement, html, nothing } from 'lit';
import '@vitesse/base-ui/elements/vitesse-dropdown';
import '@vitesse/base-ui/elements/vitesse-icon';
import type { DropdownItem } from '@vitesse/base-ui/elements/vitesse-dropdown';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { getSession } from '@vitesse/base-ui/session';
import type { AiApi } from '@vitesse/base-ui/ai';

/*
 * <vitesse-model-arming> — WHO this model acts as when it runs unattended, as one pill in the model
 * page's tab row (BasePage.renderTabTrailing).
 *
 * A statechart can invoke the AI plane and, through it, the org's integrations. Those are one
 * PERSON's identities: somebody's Linear, somebody's name in the vendor's audit log. So a model
 * that may act needs a person who accepted that, and this is where they accept it.
 *
 * ARMING IS ALWAYS SELF. The button says "act as me" and the plane records the caller — there is no
 * field for volunteering a colleague, because a record of consent that someone else could write
 * would not be a record of consent. Disarming is the armed person's own right or an org admin's:
 * withdrawing authority must never require the authority being withdrawn, so a departed colleague's
 * automation can always be stood down.
 *
 * NOTHING WITHOUT THE AI PLANE. Absent the api handle the control does not render: the AI activity
 * tile already says the plane is not connected, and a status that cannot be read is not shown.
 *
 * THE HOVER EXPLAINS, THE CLICK ACTS. The pill's label is a fact ("Acts as you"), so a click on it
 * must not change that fact: it opens a menu with the explanation and the actions. A menu, not a
 * button per action, because an admin looking at a colleague's arming has two (take it over, or
 * withdraw it), and not a hover card, because a hover cannot be reached by keyboard or by touch.
 */
@customElement('vitesse-model-arming')
export class ModelArming extends LitElement {
  /** The AI plane's client. The arming lives there, not on the fabric: the plane is the only side
   *  that can bind it to a verified session. */
  @property({ attribute: false }) api?: AiApi;
  @property() modelId = '';
  /** Who is looking, so the status can say "you" rather than a name the reader has to match to
   *  themselves — and so Disarm knows whether it is withdrawing its own authority. */
  @property() userId = '';
  @property({ type: Boolean }) canAdmin = false;

  /** False until the first read answers: no status is shown before it is known. */
  @state() private known = false;
  @state() private armedName = '';
  @state() private armedUserId = '';
  @state() private busy = false;
  @state() private err = '';

  // Light DOM, so the global model-detail SCSS styles it beside the pill tabs.
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  protected updated(changed: Map<string, unknown>): void {
    // The modelId arrives after mount on a deep link; refetch when it changes rather than showing
    // another model's answer.
    if (changed.has('modelId') || changed.has('api')) void this.refresh();
  }

  private async refresh(): Promise<void> {
    if (!this.api || !this.modelId) return;
    const modelId = this.modelId;
    try {
      const mine = (await this.api.listArmings()).find((a) => a.modelId === modelId);
      if (modelId !== this.modelId) return; // another model was opened while this one was read
      this.armedName = mine?.userName ?? '';
      this.armedUserId = mine?.userId ?? '';
      this.known = true;
    } catch (e) {
      this.err = e instanceof Error ? e.message : String(e);
    }
  }

  private async set(disarm: boolean): Promise<void> {
    if (!this.api || this.busy) return;
    this.busy = true;
    this.err = '';
    try {
      const res = await this.api.armModel(this.modelId, disarm);
      if (!res.ok) this.err = res.message;
      await this.refresh();
    } finally {
      this.busy = false;
    }
  }

  render() {
    if (!this.api || (!this.known && !this.err)) return nothing;
    const armed = !!this.armedUserId;
    const isMe = armed && this.armedUserId === this.userId;
    const name = this.armedName || this.armedUserId;
    // Disarm is the armed person's or an admin's. Rendering it for anyone else would offer a
    // control the plane will refuse — the refusal is the boundary, this is the courtesy.
    const mayDisarm = armed && (isMe || this.canAdmin);
    // Arming is gated by the same annotation <vitesse-gated> reads. A menu item is not a template
    // it can wrap, so the menu asks the session itself, and says why when the answer is no.
    const session = getSession();
    const mayArm = !isMe && session?.allowsRpc(AiAdmin.method.armModel) !== false;
    const locked = !isMe && !mayArm ? session?.whyRpc(AiAdmin.method.armModel) : undefined;
    const items: DropdownItem[] = [];
    if (mayArm) items.push({ value: 'arm', label: t('model.armAsMe'), icon: 'user-check' });
    if (mayDisarm) items.push({ value: 'disarm', label: t('model.disarm'), icon: 'ban', danger: true, divider: mayArm });
    const hint = armed ? (isMe ? t('model.armedHintSelf') : t('model.armedHint', { name })) : t('model.notArmedHint');
    return html`
      ${this.known
        ? html`<vitesse-dropdown
            align="left"
            triggerIcon="user-check"
            .triggerLabel=${armed ? (isMe ? t('model.actsAsYou') : t('model.actsAsName', { name })) : t('model.notArmed')}
            .triggerStatus=${armed ? 'green' : 'gray'}
            .triggerTitle=${hint}
            .items=${items}
            @dropdown-select=${(e: CustomEvent<{ value: string }>) => void this.set(e.detail.value === 'disarm')}
          >
            <div slot="header" class="model-arming-hint ${items.length ? '' : 'is-alone'}">
              ${hint}
              ${locked
                ? html`<span class="model-arming-locked"><vitesse-icon name="lock" size="12"></vitesse-icon>${locked}</span>`
                : nothing}
            </div>
          </vitesse-dropdown>`
        : nothing}
      ${this.err ? html`<span class="model-arming-error" title=${this.err}>${this.err}</span>` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-arming': ModelArming;
  }
}
